"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { BookUser, Building2, Car, CircleAlert, ClipboardCheck, FileCheck2, Landmark, Plus, RotateCw, ShieldCheck, Table2 } from "lucide-react";
import type { Apolice, Averbacao, Evento, Motorista, PagamentoTac, Quitacao, TabelaFrete, Tomador, Veiculo } from "@/types";
import type { ScreenKey } from "@/core/screen-meta";
import { openScreen } from "@/core/tabs";
import { useCan } from "@/core/session";
import { api } from "@/lib/api";
import { useApiMutation, useApiQuery } from "@/lib/query";
import { fmt } from "@/lib/format";
import { resolvePeriodo } from "@/lib/periodo";
import { STATUS_AVERBACAO } from "@/lib/status";
import { StatusBadge, ToneBadge, ToneIcon } from "@/components/status";
import { VIAGEM_KEYS } from "@/features/viagens/api";
import { Chips, IconButton, List, ListSkeleton, MEmpty, Page, Row, SearchField, useInfinite } from "../ui";

/* =====================================================================
 * Listas mobile genéricas (configuração por tipo).
 * Abrir um item usa openScreen(), que no mobile vira navegação em pilha.
 * ===================================================================== */

interface Item {
  titulo: React.ReactNode;
  sub?: React.ReactNode;
  trailing?: React.ReactNode;
}

interface ListaCfg<T> {
  titulo: string;
  icon: LucideIcon;
  path: string;
  query?: Record<string, string | undefined>;
  chips?: { value: string; label: string; filtro: (r: T) => boolean; tom?: "danger" | "warning" }[];
  busca: (r: T) => string;
  item: (r: T) => Item;
  abrir?: (r: T) => void;
  /** Formulário de novo registro (desktop hospedado). */
  novo?: ScreenKey;
  vazio: string;
}

const ativo = (a: boolean) => <ToneBadge tom={a ? "success" : "neutral"}>{a ? "Ativo" : "Inativo"}</ToneBadge>;
const chipsAtivos = <T extends { ativo: boolean }>(): ListaCfg<T>["chips"] => [
  { value: "todos", label: "Todos", filtro: () => true },
  { value: "ativos", label: "Ativos", filtro: (r) => r.ativo },
  { value: "inativos", label: "Inativos", filtro: (r) => !r.ativo },
];

const cfgAverbacoes: ListaCfg<Averbacao> = {
  titulo: "Averbações",
  icon: ShieldCheck,
  path: "/averbacoes",
  chips: [
    { value: "todas", label: "Todas", filtro: () => true },
    { value: "erro", label: "Com erro", filtro: (r) => r.situacao === "erro", tom: "danger" },
    { value: "averbado", label: "Averbadas", filtro: (r) => r.situacao === "averbado" },
  ],
  busca: (r) => `${r.cteNumero} ${r.averbadora} ${r.numero ?? ""} ${r.motivo ?? ""}`,
  item: (r) => ({
    titulo: `CT-e ${fmt.int(r.cteNumero)} · ${r.averbadora}`,
    sub: r.motivo ?? `${fmt.dateTime(r.data)} · ${fmt.money(r.valor)}`,
    trailing: <StatusBadge def={STATUS_AVERBACAO[r.situacao]} />,
  }),
  abrir: (r) => openScreen("cte", { id: r.cteId, numero: String(r.cteNumero) }),
  vazio: "Nenhuma averbação",
};

const cfgEventos: ListaCfg<Evento> = {
  titulo: "Eventos e rejeições",
  icon: CircleAlert,
  path: "/eventos",
  query: resolvePeriodo("30d"),
  chips: [
    { value: "todos", label: "Todos", filtro: () => true },
    { value: "rejeicoes", label: "Rejeições e erros", filtro: (r) => r.tom === "danger", tom: "danger" },
  ],
  busca: (r) => `${r.documentoRef} ${r.tipo} ${r.descricao} ${r.codigo ?? ""}`,
  item: (r) => ({
    titulo: `${r.tipo} · ${r.documentoRef}`,
    sub: `${r.codigo ? `${r.codigo} · ` : ""}${r.descricao}`,
    trailing: (
      <span className="flex flex-col items-end gap-1">
        <ToneIcon tom={r.tom} />
        <span className="text-2xs text-subtle tnum">{fmt.dayShort(r.data)}</span>
      </span>
    ),
  }),
  abrir: (r) => r.viagemId && openScreen("viagem", { id: r.viagemId }),
  vazio: "Nenhum evento",
};

const cfgPagamentos: ListaCfg<PagamentoTac> = {
  titulo: "Pagamentos TAC",
  icon: Landmark,
  path: "/financeiro/pagamentos-tac",
  query: resolvePeriodo("mes"),
  chips: [
    { value: "todos", label: "Este mês", filtro: () => true },
    { value: "saldo", label: "Com saldo", filtro: (r) => r.saldo > 0 },
  ],
  busca: (r) => `${r.nome} ${r.cpf}`,
  item: (r) => ({
    titulo: r.nome,
    sub: `${r.viagens} viagens · líquido ${fmt.money(r.liquido)}`,
    trailing: (
      <span className="flex flex-col items-end">
        <span className="text-[15px] font-semibold tnum">{fmt.money(r.saldo)}</span>
        <span className="text-2xs text-subtle">saldo</span>
      </span>
    ),
  }),
  vazio: "Nenhum pagamento no mês",
};

const cfgQuitacoes: ListaCfg<Quitacao> = {
  titulo: "Quitações de CIOT",
  icon: ClipboardCheck,
  path: "/financeiro/quitacoes",
  query: resolvePeriodo("30d"),
  busca: (r) => `${r.tacNome} ${r.ciotNumero}`,
  item: (r) => ({
    titulo: r.tacNome,
    sub: `CIOT ${r.ciotNumero} · ${fmt.dateTime(r.data)} · ${r.forma.toUpperCase()}`,
    trailing: <span className="text-[15px] font-semibold tnum">{fmt.money(r.valor)}</span>,
  }),
  abrir: (r) => openScreen("ciot", { id: r.ciotId, numero: r.ciotNumero }),
  vazio: "Nenhuma quitação nos últimos 30 dias",
};

const cfgTomadores: ListaCfg<Tomador> = {
  titulo: "Tomadores",
  icon: Building2,
  path: "/tomadores",
  chips: chipsAtivos<Tomador>(),
  busca: (r) => `${r.razaoSocial} ${r.nomeFantasia} ${r.documento}`,
  item: (r) => ({ titulo: r.razaoSocial, sub: `${fmt.doc(r.documento)} · ${r.endereco.municipio}/${r.endereco.uf}`, trailing: r.ativo ? null : ativo(false) }),
  abrir: (r) => openScreen("tomador", { id: r.id, nome: r.nomeFantasia || r.razaoSocial }),
  novo: "tomador",
  vazio: "Nenhum tomador",
};

const cfgVeiculos: ListaCfg<Veiculo> = {
  titulo: "Veículos",
  icon: Car,
  path: "/veiculos",
  chips: [
    { value: "todos", label: "Todos", filtro: () => true },
    { value: "tracao", label: "Trações", filtro: (r) => r.tipo === "tracao" },
    { value: "reboque", label: "Reboques", filtro: (r) => r.tipo === "reboque" },
  ],
  busca: (r) => `${r.placa} ${r.renavam} ${r.proprietarioNome}`,
  item: (r) => ({
    titulo: <span className="font-mono">{r.placa}</span>,
    sub: `${r.tipo === "tracao" ? "Tração" : "Reboque"} · ${r.eixos} eixos · ${r.proprietario === "proprio" ? "Próprio" : r.proprietarioNome}`,
    trailing: r.ativo ? null : ativo(false),
  }),
  abrir: (r) => openScreen("veiculo", { id: r.id, nome: r.placa }),
  novo: "veiculo",
  vazio: "Nenhum veículo",
};

const cfgMotoristas: ListaCfg<Motorista> = {
  titulo: "Motoristas e TACs",
  icon: BookUser,
  path: "/motoristas",
  chips: [
    { value: "todos", label: "Todos", filtro: () => true },
    { value: "tac", label: "TACs", filtro: (r) => r.tac },
    { value: "agregado", label: "Agregados", filtro: (r) => r.tacAgregado },
  ],
  busca: (r) => `${r.nome} ${r.cpf} ${r.rntrc}`,
  item: (r) => ({ titulo: r.nome, sub: `${fmt.cpf(r.cpf)} · CNH ${r.cnhCategoria} · ${r.tac ? (r.tacAgregado ? "TAC agregado" : "TAC") : "Funcionário"}`, trailing: r.ativo ? null : ativo(false) }),
  abrir: (r) => openScreen("motorista", { id: r.id, nome: r.nome }),
  novo: "motorista",
  vazio: "Nenhum motorista",
};

const cfgTabelas: ListaCfg<TabelaFrete> = {
  titulo: "Tabelas de frete",
  icon: Table2,
  path: "/tabelas-frete",
  chips: chipsAtivos<TabelaFrete>(),
  busca: (r) => `${r.nome} ${r.tomador?.nome ?? ""}`,
  item: (r) => ({ titulo: r.nome, sub: `${r.tomador?.nome ?? "Todos os clientes"} · ${r.rotas} ${r.rotas === 1 ? "rota" : "rotas"}`, trailing: r.ativo ? null : ativo(false) }),
  abrir: (r) => openScreen("tabela-frete", { id: r.id, nome: r.nome }),
  novo: "tabela-frete",
  vazio: "Nenhuma tabela",
};

const cfgApolices: ListaCfg<Apolice> = {
  titulo: "Apólices",
  icon: FileCheck2,
  path: "/apolices",
  chips: chipsAtivos<Apolice>(),
  busca: (r) => `${r.seguradora} ${r.numero} ${r.ramo}`,
  item: (r) => ({ titulo: `${r.seguradora} · ${r.ramo}`, sub: `${r.numero} · até ${fmt.date(r.vigenciaFim)}`, trailing: r.ativo ? null : ativo(false) }),
  abrir: (r) => openScreen("apolice", { id: r.id, nome: r.numero }),
  novo: "apolice",
  vazio: "Nenhuma apólice",
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LISTAS: Record<string, ListaCfg<any>> = {
  averbacoes: cfgAverbacoes,
  eventos: cfgEventos,
  "pagamentos-tac": cfgPagamentos,
  quitacoes: cfgQuitacoes,
  tomadores: cfgTomadores,
  veiculos: cfgVeiculos,
  motoristas: cfgMotoristas,
  "tabelas-frete": cfgTabelas,
  apolices: cfgApolices,
};

function norm(s: string) {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function MLista({ params }: { params: Record<string, string> }) {
  const tipo = params.tipo;
  const cfg = LISTAS[tipo];
  const podeEditar = useCan("cadastro.editar");
  const podeEmitir = useCan("viagem.emitir");
  const [chip, setChip] = React.useState(params.erro === "1" ? "erro" : cfg?.chips?.[0]?.value ?? "");
  const [busca, setBusca] = React.useState("");
  // Primeiro segmento = mesma chave do desktop, para as invalidações valerem aqui também.
  const q = useApiQuery<unknown[]>([tipo, "mobile", cfg?.query ?? null], cfg?.path ?? null, { query: cfg?.query });
  const reenviar = useApiMutation((ids: string[]) => api<{ reenviadas: number }>("/averbacoes/reenviar", { method: "POST", body: { ids } }), {
    invalidate: [...VIAGEM_KEYS, "averbacoes"],
    success: (r) => `${r.reenviadas} averbação(ões) reenviada(s)`,
  });

  const rows = React.useMemo(() => {
    if (!q.data || !cfg) return undefined;
    const filtro = cfg.chips?.find((c) => c.value === chip)?.filtro ?? (() => true);
    const b = norm(busca.trim());
    return q.data.filter(filtro).filter((r) => !b || norm(cfg.busca(r)).includes(b));
  }, [q.data, cfg, chip, busca]);
  const { visiveis, sentinel } = useInfinite(rows);

  if (!cfg) return <Page back title="Lista">{null}</Page>;
  const erros = tipo === "averbacoes" ? (q.data as Averbacao[] | undefined)?.filter((a) => a.situacao === "erro").map((a) => a.id) ?? [] : [];

  return (
    <Page
      back
      title={cfg.titulo}
      subtitle={rows ? `${fmt.int(rows.length)} registros` : undefined}
      onRefresh={() => q.refetch()}
      actions={
        cfg.novo && podeEditar ? (
          <IconButton icon={Plus} label="Novo" onClick={() => openScreen(cfg.novo!)} />
        ) : tipo === "averbacoes" && erros.length && podeEmitir ? (
          <IconButton icon={RotateCw} label="Reenviar com erro" onClick={() => reenviar.mutate(erros)} />
        ) : null
      }
    >
      <div className="sticky top-0 z-10 space-y-2.5 bg-app px-4 pt-3 pb-2">
        <SearchField value={busca} onChange={setBusca} />
        {cfg.chips ? (
          <Chips
            value={chip}
            onChange={setChip}
            options={cfg.chips.map((c) => ({ value: c.value, label: c.label, count: q.data ? q.data.filter(c.filtro).length : undefined, tom: c.tom && q.data?.some(c.filtro) ? c.tom : undefined }))}
          />
        ) : null}
      </div>
      <div className="px-4 pt-1 pb-6">
        {!rows ? (
          <ListSkeleton />
        ) : !rows.length ? (
          <MEmpty icon={cfg.icon} text={busca ? "Nenhum resultado" : cfg.vazio} />
        ) : (
          <List>
            {visiveis?.map((r, i) => {
              const it = cfg.item(r);
              return <Row key={(r as { id?: string; motoristaId?: string }).id ?? (r as { motoristaId?: string }).motoristaId ?? i} title={it.titulo} subtitle={it.sub} trailing={it.trailing} onClick={cfg.abrir ? () => cfg.abrir!(r) : undefined} />;
            })}
          </List>
        )}
        {sentinel}
      </div>
    </Page>
  );
}
